import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Plus, Edit2, Trash2, Copy, RefreshCw,
  MapPin, Home, Key, Users, Settings, ChevronRight, Check,
  Send, Clock, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import { API, useAuth } from '../context/AuthContext';
import { trackEvent } from '@/lib/posthog';

// ── Unit Config Dialog ─────────────────────────────────────
const UnitConfigDialog = ({ building, onClose }) => {
  const [tab, setTab]         = useState('bulk');
  const [towers, setTowers]   = useState('');
  const [floors, setFloors]   = useState(5);
  const [perFloor, setPerFloor] = useState(4);
  const [startFloor, setStart]  = useState(1);
  const [loading, setLoading] = useState(false);

  const handleBulk = async () => {
    setLoading(true);
    try {
      const payload = {
        building_id: building.id,
        towers: towers ? towers.split(',').map(t => t.trim()).filter(Boolean) : [],
        floors: parseInt(floors),
        units_per_floor: parseInt(perFloor),
        start_floor: parseInt(startFloor),
      };
      const { data } = await axios.post(`${API}/buildings/${building.id}/units/bulk`, payload);
      toast.success(`${data.created} unidades creadas. Total: ${data.total}`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar unidades');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurar unidades — {building.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="font-medium">Torres (separadas por coma, opcional)</Label>
            <Input value={towers} onChange={e => setTowers(e.target.value)} placeholder="Torre A, Torre B" className="mt-1 bg-slate-50" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="font-medium">Pisos</Label>
              <Input type="number" min={1} value={floors} onChange={e => setFloors(e.target.value)} className="mt-1 bg-slate-50" />
            </div>
            <div>
              <Label className="font-medium">Apt/Piso</Label>
              <Input type="number" min={1} value={perFloor} onChange={e => setPerFloor(e.target.value)} className="mt-1 bg-slate-50" />
            </div>
            <div>
              <Label className="font-medium">Piso inicial</Label>
              <Input type="number" min={1} value={startFloor} onChange={e => setStart(e.target.value)} className="mt-1 bg-slate-50" />
            </div>
          </div>
          <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
            Se generarán <strong>
              {(towers ? towers.split(',').filter(t => t.trim()).length || 1 : 1) * floors * perFloor}
            </strong> unidades.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
          <Button onClick={handleBulk} disabled={loading} className="bg-accent hover:bg-accent/90 text-white">
            {loading ? 'Generando...' : 'Generar unidades'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Building Form Dialog (for editing - admin) ───────────────────────────────────
const BuildingFormDialog = ({ building, onClose }) => {
  const isEdit = !!building;
  const logoRef = useRef(null);
  const [form, setForm] = useState({
    name:     building?.name     || '',
    address:  building?.address  || '',
    city:     building?.city     || '',
    logo_url: building?.logo_url || '',
  });
  const [logoPreview, setLogoPreview] = useState(building?.logo_url || '');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) { toast.error('El logo no puede superar 500KB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setLogoPreview(ev.target.result); set('logo_url', ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(`${API}/buildings/${building.id}`, form);
      toast.success('Conjunto actualizado');
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar conjunto</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2" data-testid="building-form">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div
              className="h-16 w-16 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center cursor-pointer hover:border-accent transition-colors overflow-hidden"
              onClick={() => logoRef.current?.click()}
            >
              {logoPreview
                ? <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" />
                : <Building2 className="h-6 w-6 text-slate-300" />
              }
            </div>
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} className="gap-1.5 text-xs">
                {logoPreview ? 'Cambiar logo' : 'Subir logo'}
              </Button>
              <p className="text-xs text-slate-400 mt-1">PNG/JPG, máx. 500KB</p>
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogo} data-testid="logo-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="font-medium">Nombre del conjunto *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} required className="mt-1 bg-slate-50" placeholder="Torres del Parque" data-testid="building-name-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-medium">Dirección *</Label>
              <Input value={form.address} onChange={e => set('address', e.target.value)} required className="mt-1 bg-slate-50" placeholder="Cra 15 # 93-50" data-testid="building-address-input" />
            </div>
            <div>
              <Label className="font-medium">Ciudad</Label>
              <Input value={form.city} onChange={e => set('city', e.target.value)} className="mt-1 bg-slate-50" placeholder="Bogotá" data-testid="building-city-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-building-button">
              {loading ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Building Request Dialog (for admin to request new building) ───────────────
const BuildingRequestDialog = ({ onClose }) => {
  const [form, setForm] = useState({
    name: '',
    address: '',
    city: '',
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.city) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/buildings/request`, form);
      toast.success('Solicitud enviada. El Super Admin la revisará pronto.');
      // Track building request (conjunto_created when approved, but track request here)
      trackEvent('conjunto_created', { ciudad: form.city });
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Solicitar nuevo conjunto</DialogTitle>
          <DialogDescription>
            Tu solicitud será revisada por el Super Admin. Recibirás un email cuando sea aprobada.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2" data-testid="building-request-form">
          <div className="space-y-4">
            <div>
              <Label className="font-medium">Nombre del conjunto *</Label>
              <Input 
                value={form.name} 
                onChange={e => set('name', e.target.value)} 
                required 
                className="mt-1 bg-slate-50" 
                placeholder="Torres del Parque" 
                data-testid="request-name-input" 
              />
            </div>
            <div>
              <Label className="font-medium">Dirección *</Label>
              <Input 
                value={form.address} 
                onChange={e => set('address', e.target.value)} 
                required 
                className="mt-1 bg-slate-50" 
                placeholder="Cra 15 # 93-50" 
                data-testid="request-address-input" 
              />
            </div>
            <div>
              <Label className="font-medium">Ciudad *</Label>
              <Input 
                value={form.city} 
                onChange={e => set('city', e.target.value)} 
                required 
                className="mt-1 bg-slate-50" 
                placeholder="Bogotá" 
                data-testid="request-city-input" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2" 
              data-testid="submit-request-button"
            >
              {loading ? 'Enviando...' : <><Send className="h-4 w-4" /> Enviar solicitud</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Building Card ──────────────────────────────────────────
const BuildingCard = ({ building, onEdit, onDelete, onConfigUnits, onRegenCode }) => {
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(building.invitation_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Código copiado al portapapeles');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden" data-testid={`building-card-${building.id}`}>
      {/* Card Header */}
      <div className="h-20 bg-gradient-to-br from-primary to-primary/80 relative">
        {building.logo_url
          ? <img src={building.logo_url} alt="Logo" className="h-full w-full object-cover opacity-30" />
          : null
        }
        <div className="absolute inset-0 flex items-center px-5 gap-3">
          <div className="h-12 w-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
            {building.logo_url
              ? <img src={building.logo_url} alt="Logo" className="h-10 w-10 object-cover rounded-lg" />
              : <Building2 className="h-6 w-6 text-white" />
            }
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-white truncate text-base">{building.name}</h3>
            {building.city && <p className="text-xs text-white/70">{building.city}</p>}
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Address */}
        <div className="flex items-start gap-2 text-sm text-slate-500">
          <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
          <span className="truncate">{building.address}</span>
        </div>

        {/* Stats */}
        <div className="flex gap-3">
          <div className="flex-1 bg-slate-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-primary">{building.units}</p>
            <p className="text-xs text-slate-500 mt-0.5">Unidades</p>
          </div>
        </div>

        {/* Invitation code */}
        <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
          <Key className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span className="text-xs text-slate-500 mr-auto">Código de invitación</span>
          <span className="text-xs font-mono font-bold text-slate-700 tracking-wider">{building.invitation_code || '—'}</span>
          <button
            type="button"
            onClick={copyCode}
            className="h-6 w-6 flex items-center justify-center rounded hover:bg-slate-200 transition-colors"
            data-testid={`copy-code-${building.id}`}
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
          </button>
          <button
            type="button"
            onClick={() => onRegenCode(building)}
            className="h-6 w-6 flex items-center justify-center rounded hover:bg-slate-200 transition-colors"
            title="Regenerar código"
            data-testid={`regen-code-${building.id}`}
          >
            <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onConfigUnits(building)}
            className="flex-1 gap-1.5 text-xs border-slate-200 hover:border-accent hover:text-accent"
            data-testid={`config-units-${building.id}`}
          >
            <Settings className="h-3.5 w-3.5" /> Unidades
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(building)}
            className="gap-1.5 text-xs border-slate-200 hover:border-accent hover:text-accent"
            data-testid={`edit-building-${building.id}`}
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(building)}
            className="gap-1.5 text-xs border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300"
            data-testid={`delete-building-${building.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────
const BuildingsPage = () => {
  const { user } = useAuth();
  const [buildings, setBuildings]     = useState([]);
  const [myRequests, setMyRequests]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [unitsTarget, setUnitsTarget] = useState(null);
  const [deleting, setDeleting]       = useState(false);

  const fetchBuildings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/buildings`);
      setBuildings(data);
      
      // Fetch my building requests if admin
      if (user?.role === 'admin') {
        try {
          const requestsRes = await axios.get(`${API}/buildings/my-requests`);
          setMyRequests(requestsRes.data);
        } catch (err) {
          console.error('Error fetching requests:', err);
        }
      }
    } catch { toast.error('Error al cargar conjuntos'); }
    finally { setLoading(false); }
  }, [user?.role]);

  useEffect(() => { fetchBuildings(); }, [fetchBuildings]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/buildings/${deleteTarget.id}`);
      setBuildings(prev => prev.filter(b => b.id !== deleteTarget.id));
      toast.success(`"${deleteTarget.name}" eliminado`);
      // Track building deletion
      trackEvent('conjunto_deleted', { building_id: deleteTarget.id });
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  const handleRegenCode = async (building) => {
    try {
      const { data } = await axios.post(`${API}/buildings/${building.id}/regenerate-code`);
      setBuildings(prev => prev.map(b => b.id === building.id ? data : b));
      toast.success('Código de invitación regenerado');
      // Track code regeneration
      trackEvent('invitation_code_regenerated', { building_id: building.id });
    } catch { toast.error('Error al regenerar código'); }
  };

  const handleFormClose = (refresh) => {
    setShowForm(false);
    setEditTarget(null);
    if (refresh) fetchBuildings();
  };

  const handleRequestClose = (refresh) => {
    setShowRequestForm(false);
    if (refresh) fetchBuildings();
  };

  const handleUnitsClose = (refresh) => {
    setUnitsTarget(null);
    if (refresh) fetchBuildings();
  };

  const getRequestStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Pendiente' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Aprobada' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Rechazada' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <badge.icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const pendingRequests = myRequests.filter(r => r.status === 'pending');

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">Mis Conjuntos</h1>
          <p className="text-text-secondary mt-1">Administra tus conjuntos residenciales, unidades y códigos de invitación.</p>
        </div>
        <Button
          onClick={() => setShowRequestForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full gap-2 self-start sm:self-auto"
          data-testid="request-building-button"
        >
          <Plus className="h-4 w-4" /> Solicitar nuevo conjunto
        </Button>
      </div>

      {/* Pending Requests Banner */}
      {pendingRequests.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <h3 className="font-semibold text-yellow-800">Solicitudes pendientes</h3>
          </div>
          <p className="text-sm text-yellow-700 mb-3">
            Tienes {pendingRequests.length} solicitud(es) de conjunto esperando aprobación del Super Admin.
          </p>
          <div className="space-y-2">
            {pendingRequests.map((req) => (
              <div key={req.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-yellow-100">
                <div>
                  <p className="font-medium text-slate-800">{req.name}</p>
                  <p className="text-xs text-slate-500">{req.address}, {req.city}</p>
                </div>
                {getRequestStatusBadge(req.status)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
          { label: 'Conjuntos', value: buildings.length, icon: Building2, color: 'text-primary' },
          { label: 'Unidades totales', value: buildings.reduce((s, b) => s + (b.units || 0), 0), icon: Home, color: 'text-blue-600' },
          { label: 'Activos', value: buildings.length, icon: Users, color: 'text-emerald-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
              <Icon className={`h-5 w-5 ${color}`} />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-slate-800">{value}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Building grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
        </div>
      ) : buildings.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-16 text-center">
          <Building2 className="h-14 w-14 text-slate-200 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No tienes conjuntos asignados</h3>
          <p className="text-slate-400 mb-6">Solicita tu primer conjunto para empezar a gestionar residentes y operaciones.</p>
          <Button onClick={() => setShowRequestForm(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full gap-2">
            <Plus className="h-4 w-4" /> Solicitar primer conjunto
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {buildings.map(b => (
            <BuildingCard
              key={b.id}
              building={b}
              onEdit={setEditTarget}
              onDelete={setDeleteTarget}
              onConfigUnits={setUnitsTarget}
              onRegenCode={handleRegenCode}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      {editTarget && (
        <BuildingFormDialog
          building={editTarget}
          onClose={handleFormClose}
        />
      )}

      {showRequestForm && (
        <BuildingRequestDialog onClose={handleRequestClose} />
      )}

      {unitsTarget && (
        <UnitConfigDialog building={unitsTarget} onClose={handleUnitsClose} />
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-building-modal">
          <DialogHeader>
            <DialogTitle>Eliminar conjunto</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl mb-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">{deleteTarget?.name}</p>
                <p className="text-sm text-slate-500">{deleteTarget?.city || deleteTarget?.address}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Esta acción eliminará el conjunto y <span className="font-semibold text-red-600">todos sus datos asociados</span> (unidades, residentes, PQRS, mantenimientos, etc.). Es irreversible.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="confirm-delete-building-btn"
            >
              {deleting ? '...' : <><Trash2 className="h-4 w-4" /> Eliminar todo</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BuildingsPage;
